/* demo:
import { Table } from '@/components';

// 模拟获取列表接口
const getList = () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        'totalNum': 2,
        'pageNum': 1,
        'pageSize': 10,
        'objectList': [
          { id: 1, name: '销售合同', update_at: '2022.9.10 19:40:32', update_user: '李辰新', permissions: [{ event: 'edit', name: '编辑' }, { event: 'del', name: '删除' }] },
          { id: 2, name: '采购合同', update_at: '2022.9.10 19:40:32', update_user: '李辰新' },
        ],
        'meta': { 'permissions': [{ 'event': 'purchaseOrder:create', 'name': '新增采购单' }] }
      });
    }, 100);
  });
};
const [tableProps] = useSearchForm(getList);
const columns: any[] = [
  { key: 'name', dataIndex: 'name', title: '类型', width: 120, },
  { key: 'dates', dataIndex: 'dates', title: '最近修改', width: 180, render: renderDate },
];
<Table
  {...tableProps}
  rowKey='id'
  dataSource={value}
  columns={columns}
/>
*/
import { Alert, Checkbox, Pagination, Popover, Space, Table as AntTable, Tooltip, Tree } from 'antd';
import type { TableProps as AntTableProps } from 'antd';
import ResizeObserver from 'resize-observer-polyfill';
import styles from './index.module.less';
import { CSSProperties, FC, Key, ReactNode, useEffect, useRef, useState } from 'react';
import cs from 'classnames';
import { SettingOutlined, VerticalAlignBottomOutlined, VerticalAlignMiddleOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';

interface TableProps extends Omit<AntTableProps<any>, 'className' | 'title'> {
  customrClassName?: string;
  actions?: ReactNode;
  title?: string;
  message?: ReactNode;
  columns?: any;
  columnsControll?: boolean;
  showBar?: boolean;
  wrapClassName?: string;
  wrapStyle?: CSSProperties;
}


const Table: FC<TableProps> = ({
  customrClassName,
  actions,
  title,
  message,
  // 提测时先隐藏有时间再来解决一下为什么树会报错 //
  columnsControll = false,
  columns = [],
  rowSelection,
  pagination,
  showBar = true,
  wrapClassName,
  wrapStyle = {},
  scroll = { x: 'max-content' },
  ...restProps }) => {
  const tableRef = useRef(null);
  const tableWrapper: any = useRef();
  const [checkedKeys, setCheckedKeys] = useState<any[]>(columns.map(item => item.dataIndex));
  const [allChecked, setAllChecked] = useState<boolean>(true);
  const [newColumns, setNewColumns] = useState<any[]>(columns);
  const tableColumns = newColumns.filter(newColumn => newColumn.checked);
  const treeMap = useRef<Map<Key, any>>(new Map());

  const onCheck = (values: any[], { node, checked }) => {
    setCheckedKeys(checkedKeys);
    const { dataIndex } = node;
    const currentIndex = treeMap.current.get(dataIndex);
    if (currentIndex === undefined) {
      return;
    }

    newColumns[currentIndex].checked = checked;
    setNewColumns([...newColumns]);
  };

  const onChange = (e: any) => {
    setAllChecked(e.target.checked);
    const tableColumns = newColumns.map(newColumn => {
      return {
        ...newColumn,
        checked: e.target.checked
      };
    });
    setNewColumns(tableColumns);

    if (!e.target.checked) {
      setCheckedKeys([]);
      return;
    }
    setCheckedKeys(newColumns.map(item => item.dataIndex));
  };


  const onDrop = info => {
    const { node, dropToGap, dragNode, } = info;

    // node         代表当前被drop 的对象
    // dragNode     代表当前需要drop 的对象
    // dropPosition 代表drop后的节点位置；不准确
    // dropToGap    代表移动到非最顶级组第一个位置

    // trueDropPosition: ( -1 | 0 | 1 ) dropPosition计算前的值，可以查看rc-tree源码;
    // -1 代表移动到最顶级组的第一个位置
    const dropKey = node.key;
    const dragKey = dragNode.key;
    const dropPos = node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
    const dragIndex = treeMap.current.get(dragKey);
    const dropIndex = treeMap.current.get(dropKey);
    newColumns.splice(dragIndex, 1);
    // 在同层拖拽没有跨层
    // 拖到同层节点的最上面并且只能是根结点
    if (!dropToGap) {
      return;
    }

    if (dropPosition === -1) {
      newColumns.splice(dropIndex, 0, dragNode!);
      setNewColumns([...newColumns]);
      return;
    }

    newColumns.splice(dropIndex, 0, dragNode!);
    setNewColumns([...newColumns]);
  };


  useEffect(() => {
    const map = new Map();

    for (let i = 0; i < newColumns.length; i++) {
      const { dataIndex } = newColumns[i] as { dataIndex: string };
      map.set(dataIndex, i);
    };

    treeMap.current = map;
  }, [newColumns]);


  useEffect(() => {
    if (!columns) {
      return;
    };

    const newColumns = columns.map(column => {
      const { fixed } = column;
      return {
        ...column,
        checked: true,
        fixed: fixed || false,
      };
    });

    setNewColumns(newColumns);
    setCheckedKeys(newColumns.map(item => item.dataIndex));
  }, [columns]);


  const handleClick = (fixed: string | undefined, dataIndex: string) => {
    const index = treeMap.current.get(dataIndex);
    if (index === undefined) {
      return;
    }
    newColumns[index].fixed = fixed;
    setNewColumns([...newColumns]);
  };

  const renderTitle = (treeNodeData: any) => {
    const { fixed, title, dataIndex } = treeNodeData;
    return (
      <>
        {title}
        <Space>
          {
            fixed !== 'left' && (
              <Tooltip title='固定在列首'>
                <VerticalAlignTopOutlined onClick={() => handleClick('left', dataIndex)} />
              </Tooltip>
            )
          }
          {
            fixed &&
            (

              <Tooltip title='不固定' >
                <VerticalAlignMiddleOutlined onClick={() => handleClick(undefined, dataIndex)} />
              </Tooltip>
            )
          }

          {fixed !== 'right' && (
            <Tooltip title='固定在列尾'>
              <VerticalAlignBottomOutlined onClick={() => handleClick('right', dataIndex)} />
            </Tooltip>
          )}

        </Space>
      </>
    );
  };

  const renderTree = (treeData, draggable?: boolean) => {

    const canDraggable = (node: any) => {
      const { dataIndex } = node;
      if (dataIndex === 'allChecked') {
        return false;
      }
      if (!draggable) {
        return false;
      }

      return true;
    };

    return (
      <Tree
        checkable
        draggable={canDraggable}
        onCheck={onCheck as any}
        checkedKeys={checkedKeys}
        treeData={treeData}
        titleRender={renderTitle}
        onDrop={onDrop}
        expandedKeys={['allChecked']}
        fieldNames={{ key: 'dataIndex' }} />
    );
  };

  const options = {
    indeterminate: checkedKeys.length !== newColumns.length
  };

  const renderAllChecked = () => {
    return (
      <Checkbox
        {...(checkedKeys.length) && options}
        checked={allChecked}
        onChange={onChange}>
        列设置
      </Checkbox>);
  };
  const target = tableWrapper?.current?.getBoundingClientRect().top;
  const maxHeight = document.body.clientHeight - target - 90 || 'calc(100vh - 400px)';

  const fixedLeftCloumns = newColumns.filter(newColumn => newColumn.fixed === 'left');
  const fixedRightColumns = newColumns.filter(newColumn => newColumn.fixed === 'right');
  const notFixedColumns = newColumns.filter(newColumn => !newColumn.fixed);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {});
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {showBar && (
        <div className={styles.tableToolbar}>
          <div className={styles.tableToolbarContainer}>
            <div className={styles.tableToolbarLeft}>
              <div className={styles.tableToolbarTitle}>{title}</div>
            </div>
            {
              actions && (
                <div className={styles.tableToolbarRight}>
                  <Space>
                    <Popover
                      placement='bottomRight'
                      content={
                        <>
                          {
                            !!fixedLeftCloumns.length &&
                            <>
                              固定在列首
                              {renderTree(fixedLeftCloumns)}
                            </>
                          }
                          <>
                            不固定
                            {renderTree(notFixedColumns, true)}
                          </>
                          {
                            !!fixedRightColumns.length &&
                            <>
                              固定在列尾
                              {renderTree(fixedRightColumns)}
                            </>
                          }
                        </>
                      }
                      title={renderAllChecked()}>
                      {!!columnsControll && <Tooltip title='列设置'>
                        <SettingOutlined />
                      </Tooltip>}
                    </Popover>
                    {actions}
                  </Space>
                </div>
              )
            }
          </div>
          {!!message && !!rowSelection?.selectedRowKeys?.length && <div className={styles['table-message']}>
            <Alert message={message} type='info' />
          </div>}
        </div>
      )}
      <div className={cs(styles.tableWrapper, wrapClassName)} style={{ maxHeight: maxHeight, minHeight: 200, ...wrapStyle }} ref={tableWrapper}>
        <AntTable
          {...restProps}
          ref={tableRef}
          className={cs(styles['customer-table'], customrClassName)}
          columns={tableColumns}
          pagination={false}
          sticky={{ offsetHeader: 0, offsetScroll: 0 }}
          rowSelection={rowSelection}
          scroll={scroll}
          bordered
        />
      </div>
      {pagination && (<div className={styles['pagination-wrap']}><Pagination {...pagination} /></div>)}
    </>
  );
};
export default Table;
